/**
 * Module pur pour la gestion des complétions PowerShell.
 * Fonctions testables, sans dépendance à Solid/DOM.
 */

export interface CompletionItem {
  completionText: string;
  listItemText: string;
  resultType: string;
  toolTip: string;
}

export interface CompletionState {
  items: CompletionItem[];
  replacementIndex: number;
  replacementLength: number;
  selectedIndex: number;
}

/**
 * Applique une complétion au texte en remplaçant
 * la plage [replacementIndex, replacementIndex+replacementLength]
 * par completionText.
 * Retourne le nouveau texte et la position du curseur.
 */
export function applyCompletion(
  input: string,
  replacementIndex: number,
  replacementLength: number,
  completionText: string,
): { text: string; cursor: number } {
  // Bornes défensives
  const safeIndex = Math.max(0, Math.min(replacementIndex, input.length));
  const safeLength = Math.max(0, Math.min(replacementLength, input.length - safeIndex));

  const before = input.slice(0, safeIndex);
  const after = input.slice(safeIndex + safeLength);
  const text = before + completionText + after;
  const cursor = safeIndex + completionText.length;

  return { text, cursor };
}

/**
 * Navigue dans un cycle avec wrap modulo.
 * Retourne 0 si total <= 0.
 */
export function cycleIndex(current: number, total: number, delta: number): number {
  if (total <= 0) return 0;
  return (current + delta + total) % total;
}

/**
 * Retourne le texte à afficher pour un CompletionItem.
 * Préfère listItemText s'il est non vide, sinon completionText.
 */
export function completionLabel(item: CompletionItem): string {
  return item.listItemText && item.listItemText.trim() ? item.listItemText : item.completionText;
}

/**
 * Icône + libellé court par type de complétion PowerShell.
 * Couvre les valeurs de l'enum CompletionResultType : Command, ParameterName
 * (options `-Force`), Variable (`$env:`, `$maVar`), ParameterValue, Property,
 * Method, ProviderItem/Container (fichiers), Type, Namespace, Keyword, History.
 */
export function completionTypeMeta(resultType: string): { icon: string; label: string } {
  switch (resultType) {
    case "Command":
      return { icon: "⚡", label: "commande" };
    case "ParameterName":
      return { icon: "−", label: "option" };
    case "ParameterValue":
      return { icon: "=", label: "valeur" };
    case "Variable":
      return { icon: "$", label: "variable" };
    case "Property":
      return { icon: "·", label: "propriété" };
    case "Method":
      return { icon: "ƒ", label: "méthode" };
    case "ProviderItem":
      return { icon: "📄", label: "fichier" };
    case "ProviderContainer":
      return { icon: "📁", label: "dossier" };
    case "Type":
      return { icon: "[]", label: "type" };
    case "Namespace":
      return { icon: "::", label: "namespace" };
    case "Keyword":
    case "DynamicKeyword":
      return { icon: "🔑", label: "mot-clé" };
    case "History":
      return { icon: "↻", label: "historique" };
    default:
      return { icon: "•", label: resultType || "texte" };
  }
}
